import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { cloneDeep, filter } from "lodash";

import DropdownButton from "../../form/DropdownButton";
import Button from "../../form/Button";
import TextArea from "components/form/TextArea";
import RadioInput from "components/form/RadioInput";
import Loader from "../Loader";
import DeletePopup from "../DeletePopup";
import QuestionDetails from "../CreatePost/QuestionDetails";
import NewPostDetails from "../CreatePost/NewPostDetails";
import PostDeletePopup from "../PostDeletePopup";

import {
    deletePost,
    fetchRequests,
    hidePost,
    sendRequests,
    storePostList,
    showSuccess,
    reportPost,
} from "store/slices";

import {
    checkEmailVerification,
    getDataFromLocalStorage,
    objectToFormData,
} from "utils/helpers";

import "./PostList.scss";
import "video-react/dist/video-react.css";
import { REPORT_REASONS } from "utils/constants";



const initialReportState = {
    openForPostId: null,
    reason: "",
    description: "",
    errors: {},
};

const PostDropdownButton = ({ onSuccess, type, elem }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const userData = getDataFromLocalStorage();

    const { userDetails, newPostList } = useSelector((state) => ({
        userDetails: state.student.userDetails,
        newPostList: state.global.postList,
    }));

    const { id, user_details, post, co_authors, author_details } = elem;

    // Permissions
    const { update: updateAccess, delete: deleteAccess } =
        userDetails?.role_data?.find((item) =>
            ["Network Management", "My Post and Networks"].includes(
                item?.permission_name
            )
        )?.sub_permission || {};

    const { id: loginUserID, user_type: userType } = userData;
    const { id: userID } = user_details;

    // State
    const [reportState, setReportState] = useState(initialReportState);
    const [postId, setPostId] = useState("");
    const [editData, setEditData] = useState("");
    const [qEditData, setQEditData] = useState(null);
    const [requestList, setRequestList] = useState([]);
    const [sendRequestLoader, setSendRequestLoader] = useState("");
    const [isRestoring, setIsRestoring] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    // Derived states
    const displayList = newPostList?.length
        ? filter(newPostList, { is_hidden: 0 })
        : [];

    const isEdit = loginUserID === userID;
    const isDelete = loginUserID === userID || ["0", "1"].includes(userType);
    const isReport = loginUserID !== userID;
    const isHidePost = loginUserID !== userID;
    const isFollow = loginUserID !== userID;
    const isExist = requestList.some((o) => `${o.id}` === `${userID}`);

    /** ------------------- Report Handlers ------------------- */
    const openReport = (postId) => {
        setReportState({ ...initialReportState, openForPostId: postId });
    };

    const validateReport = () => {
        const errors = {};
        if (!reportState.reason) errors.reason = "Please select a reason";
        // if (
        //     reportState.reason &&
        //     (!reportState.description ||
        //         reportState.description.trim().length < 5)
        // ) {
        //     errors.description = "Description should be at least 5 characters";
        // }
        setReportState((prev) => ({ ...prev, errors }));
        return Object.keys(errors).length === 0;
    };

    const handleReportChange = (key, value) => {
        setReportState((prev) => ({
            ...prev,
            [key]: value,
            errors: { ...prev.errors, [key]: "" },
        }));
    };

    const handleReportPost = async () => {
        if (!validateReport()) return;
        setIsLoading(true)
        const payload = {
            post_id: reportState.openForPostId,
            report_reason: reportState.reason,
            report_comment: reportState.description,
            user_id: loginUserID,
        };

        const response = await dispatch(reportPost(objectToFormData(payload)));
        if (response?.status === 200) {
            dispatch(showSuccess(response?.message || "Post reported successfully"));
            setReportState(initialReportState);
            if (reportState.reason === 2 || reportState.reason === 5) {
                const updatedPostList = displayList.filter(
                    (item) => item.id !== reportState.openForPostId
                );
                dispatch(storePostList(updatedPostList));
            }

        }
        setIsLoading(false)
    };

    /** ------------------- Post Actions ------------------- */
    const handleRestore = async (id) => {
        setIsRestoring(true);
        const payload = { post_id: id, key: "restore" };
        const response = await dispatch(deletePost(payload));
        if (response?.status === 200) {
            dispatch(storePostList(displayList.filter((o) => o.id !== id)));
            dispatch(showSuccess(response.message));
        }
        setIsRestoring(false);
    };

    const handelHidePost = async (postID) => {
        const payload = { post_id: postID, user_id: loginUserID, type: "hide" };
        const response = await dispatch(hidePost(objectToFormData(payload)));
        if (response?.status === 200) onSuccess();
    };

    const handelSendRequest = async (id) => {
        setSendRequestLoader(id);
        const response = await dispatch(
            sendRequests(objectToFormData({ receiver_id: id }))
        );
        if (response?.status === 200) {
            let oldList = cloneDeep(requestList);
            oldList = oldList.find((o) => `${o.id}` === `${id}`)
                ? oldList.filter((o) => `${o.id}` !== `${id}`)
                : [...oldList, response?.data];
            setRequestList(oldList);
        }
        setSendRequestLoader("");
    };

    return (
        <>
            {editData && (
                <>
                    <NewPostDetails
                        type={type}
                        editData={editData}
                        onHide={() => {
                            setEditData('');
                        }}
                        handelSuccess={() => {
                            setEditData('');
                        }}
                    />
                </>
            )}
            {qEditData && (
                <QuestionDetails
                    editData={qEditData}
                    onHide={() => {
                        setQEditData(null);
                    }}
                    handelSuccess={() => {
                        setQEditData(null);
                    }}
                />
            )}
            {postId && (["0", "1"].includes(userType) ?
                <PostDeletePopup onHide={() => {
                    setPostId(null);
                }
                } handelSuccess={() => {
                    const oldPostList = [...displayList].filter((o) => o.id !== postId);
                    dispatch(storePostList(oldPostList));
                    setPostId(null);
                }}
                    postId={postId}
                />
                :
                <DeletePopup
                    title="Delete Post"
                    message="Are you sure you want to delete this post?"
                    id={postId}
                    onHide={() => {
                        setPostId(null);
                    }}
                    handelSuccess={() => {
                        const oldPostList = [...displayList].filter((o) => o.id !== postId);
                        dispatch(storePostList(oldPostList));
                        setPostId(null);
                    }}
                    handelDelete={async () => {
                        let forData = objectToFormData({ post_id: postId, key: 'delete' });
                        const response = await dispatch(deletePost(forData));
                        return response;
                    }}
                />
            )}

            <DropdownButton
                parentClass="post-options post-dropdown"
                className="d-flex justify-content-end"
                id="iconType"
                icon={
                    <i className="bi bi-three-dots-vertical text-18-400 color-gray pointer" />
                }
            >

                {reportState.openForPostId === id ? (
                    <div className="cps-20 cpe-20 cpt-10 cpb-10">
                        <div className="text-14-500-18 cmb-5">Why are you reporting this post?</div>
                        {REPORT_REASONS.map((reason) => (
                            <div key={reason.value} className="cmb-12">
                                <RadioInput
                                    label={reason.label}
                                    labelClassName="color-black-olive text-14-500-18 cps-3"
                                    value={reason.value}
                                    name="reason"
                                    checked={reportState.reason === reason.value}
                                    onChange={() => handleReportChange("reason", reason.value)}
                                />

                            </div>
                        ))}

                        {reportState.errors.reason && (
                            <div className="text-danger text-12 mb-1">{reportState.errors.reason}</div>
                        )}

                        <TextArea
                            placeholder="Description"
                            id="description"
                            value={reportState.description}
                            onChange={(e) => handleReportChange('description', e.target.value)}
                        />
                        {reportState.errors.description && (
                            <div className="text-danger text-12 mt-1">{reportState.errors.description}</div>
                        )}

                        <div className="d-flex flex-row gap-2 cmt-10">
                            <Button
                                text={!isLoading && `Report Post`}
                                btnStyle="primary-dark"
                                className="h-35 cps-20 cpe-20"
                                onClick={handleReportPost}
                                btnLoading={isLoading}
                                disabled={isLoading}
                            />
                            <Button
                                text="Close"
                                btnStyle="primary-gray"
                                className="h-35 cps-20 cpe-20"
                                onClick={() => setReportState({ ...reportState, openForPostId: null })}
                            />
                        </div>
                    </div>
                ) :
                    <div className="post-options-list">
                        {type === 'deleted-posts' ? <>
                            {["0", "1"].includes(userType) && updateAccess === '1' && (
                                <div
                                    className="text-14-400 pointer color-black-olive"
                                    onClick={() => {
                                        handleRestore(id);
                                    }}
                                >
                                    {isRestoring && postId === id ? "Restoring..." : "Restore"}

                                </div>
                            )}
                        </> :
                            <>
                                {isEdit && (["0", "1", "6"].includes(userType) ? updateAccess === '1' : true) && (
                                    <div
                                        className="text-14-400 pointer color-black-olive"
                                        onClick={() => {
                                            if (elem?.category_id === '8') {
                                                setQEditData(elem);
                                            } else {
                                                setEditData(elem);
                                            }
                                        }}
                                    >
                                        Edit
                                    </div>
                                )}

                                {isFollow && !isExist && (
                                    <div
                                        className="text-14-400 pointer color-black-olive d-flex align-items-center justify-content-between gap-3"
                                        onClick={() => {
                                            if (
                                                !checkEmailVerification(
                                                    dispatch,
                                                    navigate
                                                )
                                            )
                                                return;
                                            handelSendRequest(userID);
                                        }}
                                    >
                                        <div className="p-0">Follow</div>
                                        {userID === sendRequestLoader && (
                                            <Loader size="sm" />
                                        )}
                                    </div>
                                )}
                                {isHidePost && (
                                    <div
                                        className="text-14-400 pointer color-black-olive"
                                        onClick={() => {
                                            handelHidePost(id);
                                        }}
                                    >
                                        Hide This Post
                                    </div>
                                )}

                                {isDelete && (["0", "1", "6"].includes(userType) ? deleteAccess === '1' : true) && (
                                    <div
                                        className="text-14-400 pointer color-black-olive"
                                        onClick={() => {
                                            setPostId(id);
                                        }}
                                    >
                                        Delete
                                    </div>
                                )}

                                {isReport &&
                                    <div
                                        className="text-14-400 pointer color-black-olive"
                                        onClick={() => openReport(id)}

                                    >
                                        Report Post
                                    </div>}
                            </>}
                    </div>}
            </DropdownButton>
        </>
    )
}

export default PostDropdownButton
